import { useRef, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Top-level Extract tool: PDF / images → plain text.
 * Not tied to a case. Operators paste the result into New case.
 */
export function ExtractPanel() {
  const auth = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (auth.status === "anon") return null;

  const onRun = async () => {
    if (busy) return;
    if (files.length === 0) {
      setError("请至少选择一个 PDF 或图片。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await api.runExtractTool({ files });
      setResult(out.text);
      downloadText(out.filename || "extract.txt", out.text);
      toast.add({
        type: "success",
        title: "提取完成",
        description: "已下载 extract.txt。创建案例时可粘贴到新建案例。",
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "extract_failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-2xl font-semibold tracking-tight">
          提取
        </h1>
        <p className="text-sm text-muted-foreground">
          从 PDF 或图片提取文本。独立工具，不绑定案例。创建案例时可将结果粘贴到新建案例。
        </p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>源文件</CardTitle>
          <CardDescription>
            上传 PDF 页面或图片。模型会返回可复制或下载的纯文本。
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="extract-files">PDF 或图片</FieldLabel>
              <input
                ref={fileRef}
                id="extract-files"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,image/*,application/pdf"
                className="block w-full text-sm"
                disabled={busy}
                onChange={(e) => {
                  setFiles(Array.from(e.target.files ?? []));
                }}
              />
              <FieldDescription>
                PDF 与图片会经提取模型处理。最大体积取决于网关限制。
              </FieldDescription>
              {files.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {files.map((f) => f.name).join(", ")}
                </p>
              ) : null}
            </Field>
            {error ? <FieldError>{error}</FieldError> : null}
          </FieldGroup>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={busy || files.length === 0}
              onClick={() => void onRun()}
            >
              {busy ? <Spinner /> : null}
              {busy ? "提取中…" : "提取为 .txt"}
            </Button>
            {result ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => downloadText("extract.txt", result)}
              >
                再次下载
              </Button>
            ) : null}
          </div>
          {result ? (
            <Alert>
              <AlertTitle>输出</AlertTitle>
              <AlertDescription>
                <Textarea
                  value={result}
                  readOnly
                  rows={16}
                  className="mt-2 max-h-96 overflow-y-auto font-mono text-xs"
                />
              </AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
